import { nextTick, reactive } from 'vue';
import i18n from '@/app/lang/i18n.js';
import { unexpectedError } from '@/shared/utils/unexpected-error.js';

const queue = [];
const state = reactive({
    isOpen: false,
    title: '',
    description: '',
    confirmLabel: i18n.global.t('general.confirm'),
    cancelLabel: i18n.global.t('general.cancel'),
    loading: false,
    currentResolve: null,
    currentHandler: null,
});

function processQueue() {
    if (state.isOpen) {
        return;
    }

    const next = queue.shift();
    if (!next) {
        return;
    }

    state.title = next.options.title || '';
    state.description = next.options.description || '';
    state.confirmLabel = next.options.confirmLabel || i18n.global.t('general.confirm');
    state.cancelLabel = next.options.cancelLabel || i18n.global.t('general.cancel');
    state.loading = false;
    state.currentHandler = next.handler;
    state.isOpen = true;
    state.currentResolve = next.resolve;
}

export function useConfirmDialog() {
    async function showConfirm(options = {}, asyncHandler = null) {
        return new Promise((resolve) => {
            queue.push({ options, handler: asyncHandler, resolve });
            processQueue();
        });
    }

    async function onConfirm() {
        if (!state.currentResolve) {
            return;
        }

        state.loading = true;
        try {
            if (state.currentHandler) {
                await state.currentHandler();
            }
            state.currentResolve(true);
        } catch (err) {
            unexpectedError(err);
            state.currentResolve(false);
        } finally {
            close();
        }
    }

    function onCancel() {
        if (state.currentResolve) {
            state.currentResolve(false);
        }
        close();
    }

    function close() {
        state.isOpen = false;
        state.loading = false;
    }

    function afterLeave() {
        state.currentResolve = null;
        state.currentHandler = null;

        // Open next dialog if queue is not empty
        void nextTick(() => processQueue());
    }

    return {
        state,
        showConfirm,
        onConfirm,
        onCancel,
        afterLeave,
    };
}
